import TextField from '@mui/material/TextField';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {StaticDatePicker} from '@mui/x-date-pickers/StaticDatePicker';
import {StaticDateRangePicker} from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import React, {useState} from "react";
import {Box, Chip, Grid, Stack, useTheme} from "@mui/material";
import Typography from "@mui/material/Typography";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import hu from "date-fns/locale/hu";
import useReservationQueries from "../../hooks/queries/useReservationQueries";
import {formatHuman} from "../../utils/dateUtils";
import {isRangeFullyAvailable} from "../../utils/reservationAvailability";
import {useSnackbar} from "notistack";
import Loading from "../Loading";

const FULL_DAY_SLOT = {start: '00:00', end: '23:59'}

const FullDayInfo = () => <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
    <Typography variant="subtitle2" align="center">Válassz egy szabad napot a naptárból!</Typography>
    <Typography variant="body2" gutterBottom align="center">A foglalás a nap egészére vonatkozik.</Typography>
</Box>

const RangeInfo = ({rangeStart, rangeEnd}) => {
    if (!rangeStart) return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Válaszd ki az időszak kezdő napját!</Typography>
        <Typography variant="body2" gutterBottom align="center">Majd válaszd ki a záró napot is a naptárból.</Typography>
    </Box>

    if (!rangeEnd) return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Kezdő nap: {formatHuman(rangeStart)}</Typography>
        <Typography variant="body2" gutterBottom align="center">Válaszd ki a záró napot is a naptárból!</Typography>
    </Box>

    return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Kiválasztott időszak</Typography>
        <Typography variant="body1" gutterBottom align="center">{formatHuman(rangeStart)} - {formatHuman(rangeEnd)}</Typography>
    </Box>
}


const SelectOnlyDate = ({reservationData, setNewDate}) => {
    const today = new Date()
    const lengthOptions = reservationData?.price?.service?.length_options || 'regular'

    const [selectedDate, setSelectedDate] = useState(new Date())
    const [selectedSlot, setSelectedSlot] = useState({})
    const [dateRange, setDateRange] = useState([null, null])
    const [month, setMonth] = useState(today.getMonth() + 1)
    const [year, setYear] = useState(today.getFullYear())
    const [loading, setLoading] = useState(false)
    const priceId = reservationData.price.id;
    const formattedDate = formatHuman(selectedDate);

    const {useFreeSlotsForDate, useMonthlyAvailability, fetchMonthlyAvailability} = useReservationQueries()
    const {enqueueSnackbar} = useSnackbar()

    const {data:slots, } = useFreeSlotsForDate(priceId, formattedDate)
    const {data: availability} = useMonthlyAvailability(priceId, year, month)

    const theme = useTheme()

    const isAvailableForReserve = (date) => {
        if (availability === undefined) return false
        if (Object.keys(availability).includes(formatHuman(date))) return !availability[formatHuman(date)]
        return false
    }

    const handleDateChange = (newValue) => {
        setSelectedDate(newValue)
        if (lengthOptions === 'full-day') {
            setNewDate({id: reservationData.id, date: formatHuman(newValue), slot: FULL_DAY_SLOT})
        }
    }

    const handleRangeChange = async (newRange) => {
        setDateRange(newRange)
        const [start, end] = newRange
        if (!start || !end) return
        setLoading(true)
        const ok = await isRangeFullyAvailable(fetchMonthlyAvailability, priceId, start, end)
            .catch(e => {
                console.error(e)
                return false
            })
            .finally(() => setLoading(false))
        if (ok) {
            setNewDate({id: reservationData.id, date: formatHuman(start), end_date: formatHuman(end), slot: FULL_DAY_SLOT})
        } else {
            enqueueSnackbar('A kiválasztott időszakban foglalt nap található, válassz másik időszakot!', {variant: 'error'})
            setDateRange([null, null])
        }
    }

    return <div className="data-container">
        <Stack direction={{xs: 'column', md: 'row'}} alignItems='center'>
            <div className="date-select-calendar">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
                    {lengthOptions === 'multi-day' ? (
                        <StaticDateRangePicker
                            displayStaticWrapperAs="desktop"
                            calendars={1}
                            disablePast={true}
                            loading={availability === undefined}
                            shouldDisableDate={isAvailableForReserve}
                            value={dateRange}
                            onChange={handleRangeChange}
                            onMonthChange={(d) => {
                                setMonth(d.getMonth() + 1)
                                setYear(d.getFullYear())
                            }}
                            renderInput={(startProps, endProps) => <>
                                <TextField {...startProps} />
                                <TextField {...endProps} />
                            </>}
                        />
                    ) : (
                        <StaticDatePicker
                            displayStaticWrapperAs="desktop"
                            disablePast={true}
                            loading={availability === undefined}
                            openTo="day"
                            shouldDisableDate={isAvailableForReserve}
                            value={selectedDate}
                            onChange={handleDateChange}
                            onMonthChange={(d) => {
                                const year = d.getFullYear()
                                const month = d.getMonth()
                                setMonth(month + 1)
                                setYear(year)
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    )}
                </LocalizationProvider>
            </div>
            {lengthOptions === 'regular' && <div className="data-container-slots" style={slots?.length !== 0 ? {} : {display: 'flex'}}>
                {slots?.length !== 0 ? <>
                        <Typography variant="subtitle2" align="center">Szabad időpontok
                            - {formatHuman(selectedDate)}</Typography>
                        <Typography variant="body2" gutterBottom align="center">Válassz szabad időpontot az alábbiak
                            közül!</Typography>
                        <Grid container spacing={1}>
                            {slots?.map((s, i) => <Grid style={{display: 'flex', justifyContent: 'center'}} item xs={4}
                                                       md={3} key={`slot_${i}`}>
                                <Chip onClick={() => {
                                    setSelectedSlot(s)
                                    setNewDate({id:reservationData.id, date:formatHuman(selectedDate), slot:s})
                                }} label={s.start + '-' + s.end} style={{
                                    color: selectedSlot?.start === s.start ? 'white' : theme.palette.primary.main,
                                    backgroundColor: selectedSlot?.start === s.start ? theme.palette.primary['400'] : theme.palette.primary['20'],
                                    fontWeight: '600',
                                }}/>
                            </Grid>)}
                        </Grid>
                    </>
                    : <div style={{alignSelf: "center"}}>
                        <Typography variant="subtitle1" align="center">Erre a napra nincsenek szabad
                            időpontok!</Typography>
                        <Typography variant="body2" gutterBottom align="center">Válassz másik időpontot!</Typography>
                    </div>}
            </div>}
            {lengthOptions === 'full-day' && <div className="data-container-slots" style={{display: 'flex'}}>
                <FullDayInfo/>
            </div>}
            {lengthOptions === 'multi-day' && <div className="data-container-slots" style={{display: 'flex'}}>
                <RangeInfo rangeStart={dateRange[0]} rangeEnd={dateRange[1]}/>
            </div>}
        </Stack>

        <Loading isLoading={loading}/>
    </div>
}

export default SelectOnlyDate
